import { ChangeDetectionStrategy, Component, signal } from '@angular/core';

interface StoryStep {
  readonly tab: string;
  readonly title: string;
  readonly description: string;
}

interface ProblemCard {
  readonly icon: string;
  readonly question: string;
  readonly keyword: string;
  readonly detail: string;
  readonly cardClass: string;
  readonly keywordClass: string;
  readonly tag?: string;
}

interface DataSource {
  readonly icon: string;
  readonly title: string;
  readonly detail: string;
}

interface ForecastRow {
  readonly icon: string;
  readonly product: string;
  readonly lastWeek: number;
  readonly quantity: number;
  readonly trendPercent: number;
}

const FORECAST_ROWS: readonly ForecastRow[] = [
  { icon: '🥖', product: 'Baguettes', lastWeek: 160, quantity: 180, trendPercent: 12 },
  { icon: '🥐', product: 'Croissants', lastWeek: 102, quantity: 110, trendPercent: 8 },
  { icon: '🥪', product: 'Sandwichs', lastWeek: 50, quantity: 65, trendPercent: 30 },
  { icon: '🍮', product: 'Flans', lastWeek: 20, quantity: 18, trendPercent: -10 },
];

const MAX_FORECAST_QUANTITY = Math.max(...FORECAST_ROWS.map((row) => row.quantity));

/** Version interactive (sans vidéo) du scénario de la vidéo de présentation. */
@Component({
  selector: 'app-demo-storyboard',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './demo-storyboard.component.html',
  styleUrl: './demo-storyboard.component.css',
})
export class DemoStoryboardComponent {
  readonly activeStep = signal(0);

  readonly steps: readonly StoryStep[] = [
    {
      tab: '🤔 Le problème',
      title: 'Chaque jour, la même question',
      description:
        'Trop préparé : invendus, gaspillage et marge perdue. Pas assez : ruptures, ventes et clients perdus.',
    },
    {
      tab: "🧠 L'analyse IA",
      title: "Votre expertise métier, augmentée par l'IA",
      description:
        "Historique des ventes, météo locale, habitudes clients, événements, calendrier et localisation : l'IA croise tout pour vous.",
    },
    {
      tab: '📊 La recommandation',
      title: 'Chaque matin, les bonnes quantités',
      description:
        'Une production recommandée produit par produit, mise à jour à 5 h, avant la première fournée.',
    },
  ];

  readonly problemCards: readonly ProblemCard[] = [
    {
      icon: '🧺',
      question: 'Trop préparé ?',
      keyword: 'Invendus',
      detail: 'gaspillage, marge perdue',
      cardClass: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20',
      keywordClass: 'text-red-600 dark:text-red-400',
    },
    {
      icon: '🍽️',
      question: 'Pas assez ?',
      keyword: 'Ruptures',
      detail: 'ventes et clients perdus',
      cardClass: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20',
      keywordClass: 'text-amber-600 dark:text-amber-400',
      tag: 'Épuisé',
    },
  ];

  readonly dataSources: readonly DataSource[] = [
    { icon: '📊', title: 'Historique des ventes', detail: 'vos données de caisse' },
    { icon: '🌤️', title: 'Météo', detail: 'prévisions locales à 7 jours' },
    { icon: '👥', title: 'Habitudes clients', detail: "jours et heures d'affluence" },
    { icon: '⭐', title: 'Événements', detail: 'marchés, matchs, fêtes' },
    { icon: '📅', title: 'Calendrier', detail: 'fériés, vacances, ponts' },
    { icon: '📍', title: 'Localisation', detail: 'zone et flux de passage' },
  ];

  readonly forecastContext: readonly string[] = [
    '☀️ Soleil, 24\u00a0°C',
    '⭐ Brocante du quartier',
    '📍 Centre-ville',
  ];

  readonly forecastRows = FORECAST_ROWS.map((row) => ({
    ...row,
    barPercent: Math.round((row.quantity / MAX_FORECAST_QUANTITY) * 100),
    trendLabel: `${row.trendPercent > 0 ? '+' : '−'}${Math.abs(row.trendPercent)}\u00a0%`,
    trendClass:
      row.trendPercent > 0
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300',
  }));

  readonly benefits: readonly string[] = [
    "Moins d'invendus",
    'Moins de ruptures',
    'Plus de marge',
  ];

  selectStep(index: number): void {
    this.activeStep.set(index);
  }
}
